import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config, { getDefaultHeaders } from '../config/api';
import { useCountry } from '../context/CountryContext';

// Create the context
const VarianceAppContext = createContext();

// Custom hook to use the context
export const useVarianceAppContext = () => {
  const context = useContext(VarianceAppContext);
  if (context === undefined) {
    throw new Error('useVarianceAppContext must be used within a VarianceAppProvider');
  }
  return context;
};

// Provider component
export const VarianceAppProvider = ({ children }) => {
  const { country } = useCountry();
  const [businessSegments, setBusinessSegments] = useState([]);
  const [selectedBusinessSegment, setSelectedBusinessSegment] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [businessSegmentsError, setBusinessSegmentsError] = useState(null);
  const [filteredDataError, setFilteredDataError] = useState(null);
  const [dataAvailable, setDataAvailable] = useState(true);
  
  // Selected financial items state
  const [selectedFinancialItems, setSelectedFinancialItems] = useState([]);

  // Fetch business segments when country changes
  useEffect(() => {
    if (country) {
      fetchBusinessSegments();
    } else {
      setBusinessSegments([]);
      setSelectedBusinessSegment(null);
      setDataAvailable(false);
    }
  }, [country]);

  // Memoize fetchBusinessSegments to prevent unnecessary re-creation
  const fetchBusinessSegments = useCallback(async () => {
    try {
      setLoading(true);
      setBusinessSegmentsError(null);
      const response = await axios.get(`${config.apiUrl}/business-segments`, {
        headers: getDefaultHeaders(country)
      });
      
      const available = response.data.available !== false;
      setDataAvailable(available);
      
      if (available) {
        const segments = response.data.business_segments || [];
        setBusinessSegments(segments);
        
        // Auto-select first business segment if available
        if (segments.length > 0 && !selectedBusinessSegment) {
          setSelectedBusinessSegment(segments[0]);
        }
      } else {
        setBusinessSegments([]);
        setSelectedBusinessSegment(null);
      }
    } catch (err) {
      setBusinessSegmentsError('Failed to fetch business segments');
      setDataAvailable(false);
      console.error('Error fetching business segments:', err);
    } finally {
      setLoading(false);
    }
  }, [country]);

  // Memoize fetchFilteredData to prevent unnecessary re-creation
  const fetchFilteredData = useCallback(async (businessSegment) => {
    try {
      setFilteredDataError(null);
      const response = await axios.get(`${config.apiUrl}/filtered-data?business_segment=${encodeURIComponent(businessSegment)}`, {
        headers: getDefaultHeaders(country)
      });
      
      if (response.data.available !== false) {
        setFilteredData(response.data.data || []);
      } else {
        setFilteredData([]);
      }
    } catch (err) {
      setFilteredDataError('Failed to fetch filtered data');
      console.error('Error fetching filtered data:', err);
    }
  }, [country]);

  // Fetch business segments when country changes
  useEffect(() => {
    if (country) {
      fetchBusinessSegments();
    } else {
      setBusinessSegments([]);
      setSelectedBusinessSegment(null);
      setDataAvailable(false);
    }
  }, [country, fetchBusinessSegments]);

  // Fetch filtered data when business segment changes
  useEffect(() => {
    if (selectedBusinessSegment && country) {
      fetchFilteredData(selectedBusinessSegment);
    }
  }, [selectedBusinessSegment, country, fetchFilteredData]);

  const refreshData = () => {
    if (selectedBusinessSegment) {
      fetchFilteredData(selectedBusinessSegment);
    }
  };

  const clearAllComponentData = () => {
    setBusinessSegments([]);
    setSelectedBusinessSegment(null);
    setFilteredData([]);
    setSelectedFinancialItems([]);
  };

  const handleBusinessSegmentChange = (segment) => {
    // Clear all component data when business segment changes
    setFilteredData([]);
    setBusinessSegmentsError(null);
    setFilteredDataError(null);
    setSelectedFinancialItems([]);
    setSelectedBusinessSegment(segment);
  };

  // Financial items management functions
  const addSelectedFinancialItem = (item) => {
    setSelectedFinancialItems(prev => 
      prev.includes(item) ? prev : [...prev, item]
    );
  };

  const removeSelectedFinancialItem = (item) => {
    setSelectedFinancialItems(prev => prev.filter(id => id !== item));
  };

  const clearSelectedFinancialItems = () => {
    setSelectedFinancialItems([]);
  };

  const value = {
    businessSegments,
    selectedBusinessSegment,
    filteredData,
    loading,
    error: businessSegmentsError || filteredDataError, // Combine errors
    dataAvailable, // Add data availability flag
    setSelectedBusinessSegment: handleBusinessSegmentChange,
    refreshData,
    clearAllComponentData,
    // Financial items state
    selectedFinancialItems,
    setSelectedFinancialItems,
    addSelectedFinancialItem,
    removeSelectedFinancialItem,
    clearSelectedFinancialItems,
  };

  return (
    <VarianceAppContext.Provider value={value}>
      {children}
    </VarianceAppContext.Provider>
  );
};

